import { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getUserRolesMapSelector } from 'store/selectors/user';
import { useSelector } from 'react-redux';
import { Calendar } from 'components/Icons';
import { Button } from 'components';
import Slider from 'react-slick';
import { ArrowRight, Photos, Close as CloseIcon } from 'components/Icons';
import { getFormattedListingDetailData } from 'store/selectors/listingDetail';
import styles from './styles.module.scss';

const renderRightNav = (props, disabled, fullScreen, isClient) =>
  !disabled && (
    <button
      {...props}
      className={
        fullScreen
          ? classNames(styles.navFullScreen, styles.rightNavFullScreen)
          : classNames(styles.nav, { [styles.navFullScreenClient]: isClient }, styles.rightNav)
      }
    >
      <ArrowRight className={styles.navIcon} color={fullScreen ? '#fff' : undefined} />
    </button>
  );

const renderLeftNav = (props, disabled, fullScreen, isClient) =>
  !disabled && (
    <button
      {...props}
      className={
        fullScreen
          ? classNames(styles.navFullScreen, styles.leftNavFullScreen)
          : classNames(styles.nav, { [styles.navFullScreenClient]: isClient }, styles.leftNav)
      }
    >
      <ArrowRight className={styles.navIcon} color={fullScreen ? '#fff' : undefined} />
    </button>
  );

const closeNav = (onClick) => (
  <button className={classNames(styles.closeNav, styles.close)} onClick={onClick}>
    <CloseIcon className={styles.closeIcon} color="#fff" />
  </button>
);

const PhotosView = ({
  photos,
  imageId,
  onScheduleTour,
  isTourScheduled,
  isFetchingTourStatus = false,
  disableScheduleTour = false,
  photosExternalLinks,
}) => {
  const sliderRef = useRef(null);
  const sliderRefFull = useRef(null);
  const thumbnailRefs = useRef([]);
  const { isClient } = useSelector(getUserRolesMapSelector);
  const [selectedImage, setSelectedImage] = useState(imageId ?? null);
  const [fullScreen, setFullScreen] = useState(false);
  const { activityStatus } = disableScheduleTour
    ? { activityStatus: '' }
    : useSelector(getFormattedListingDetailData);
  const isPropertyClosed = useMemo(() => {
    return activityStatus?.trim()?.toLowerCase() === 'closed';
  }, [activityStatus]);

  const closeFullScreen = () => {
    setFullScreen(false);
  };

  useEffect(() => {
    const el = thumbnailRefs.current[selectedImage];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [selectedImage]);

  useEffect(() => {
    let lastKeyTime = 0;
    const delay = 10;
    const handleKeyDown = (event) => {
      const now = Date.now();

      if (now - lastKeyTime < delay) return;
      lastKeyTime = now;

      if (event.key === 'ArrowLeft') {
        sliderRef.current?.slickPrev();
      } else if (event.key === 'ArrowRight') {
        sliderRef.current?.slickNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      sliderRef.current?.slickGoTo(selectedImage);
    }, 1);
  }, [fullScreen]);

  const CarouselNext = ({ ...props }) => (
    <>
      {renderRightNav(
        props,
        selectedImage === photos.length - 1,
        fullScreen,
        isClient && !disableScheduleTour,
      )}
    </>
  );

  const CarouselPrev = ({ ...props }) => (
    <>{renderLeftNav(props, selectedImage === 0, fullScreen, isClient && !disableScheduleTour)}</>
  );

  const handleBeforeChange = (_, newIndex) => {
    setSelectedImage(newIndex);
  };

  const settings = {
    draggable: true,
    accessibility: false,
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <CarouselNext />,
    prevArrow: <CarouselPrev />,
    beforeChange: handleBeforeChange,
  };

  return (
    <div testid="image_gallery" className={styles.container} onClick={(e) => e.stopPropagation()}>
      <div className={styles.leftColumn}>
        {photos.map((photo, index) => (
          <img
            key={index}
            ref={(el) => (thumbnailRefs.current[index] = el)}
            src={photo}
            alt={`Image ${index + 1}`}
            className={selectedImage === index ? styles.activeImage : ''}
            onClick={() => {
              if (photosExternalLinks.length) {
                const found = photosExternalLinks.find((item) => item.index === index);
                if (found) {
                  window.open(found.link, '_blank');
                  return;
                }
              }

              setSelectedImage(index);
              sliderRef.current?.slickGoTo(index);
            }}
          />
        ))}
      </div>
      <div className={styles.rightColumn}>
        {fullScreen ? closeNav(closeFullScreen) : null}

        <div className={classNames(styles.counter, { [styles.counterClient]: isClient })}>
          <Photos className={styles.counterIcon} />
          <span testid="count" className={styles.count}>{`${selectedImage + 1} / ${
            photos?.length
          }`}</span>
        </div>
        {fullScreen ? (
          <div className={styles.fullScreen}>
            <Slider {...settings} className={classNames(styles.slider)} ref={sliderRefFull}>
              {photos.map((photo) => (
                <img
                  src={photo}
                  alt="Full Size Image"
                  className={styles.rightColumnImgFullScreen}
                />
              ))}
            </Slider>
          </div>
        ) : (
          <Slider {...settings} className={classNames(styles.slider)} ref={sliderRef}>
            {photos.map((photo, index) => (
              <img
                src={photo}
                alt="Full Size Image"
                className={styles.rightColumnImg}
                onClick={() => {
                  if (photosExternalLinks.length) {
                    const found = photosExternalLinks.find((item) => item.index === index);
                    if (found) {
                      window.open(found.link, '_blank');
                      return;
                    }
                  }

                  setFullScreen(true);
                  setTimeout(() => {
                    sliderRefFull.current?.slickGoTo(selectedImage);
                  }, 1);
                }}
              />
            ))}
          </Slider>
        )}
        {isClient && !fullScreen && !isPropertyClosed && !disableScheduleTour ? (
          <div className={styles.buttonContainer}>
            <div className={styles.buttonWrapper}>
              <Button
                className={classNames(styles.homeActionsButton, styles.lastButton)}
                title={isTourScheduled ? 'Tour Requested' : 'Request Tour'}
                icon={<Calendar className={styles.buttonIcon} color={'#fff'} />}
                titleClassName={styles.buttonTitle}
                testid="schedule_tour"
                onClick={onScheduleTour && onScheduleTour}
                disabled={isTourScheduled}
                isPending={isFetchingTourStatus}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

PhotosView.propTypes = {
  imageId: PropTypes.number,
  photos: PropTypes.arrayOf(PropTypes.string).isRequired,
  onScheduleTour: PropTypes.func,
  isTourScheduled: PropTypes.bool,
  isFetchingTourStatus: PropTypes.bool,
  disableScheduleTour: PropTypes.bool,
  photosExternalLinks: PropTypes.array,
};

export default PhotosView;
